import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect } from 'react';
import ImageResize from 'tiptap-extension-resize-image';
import { AddCircleIcons } from '../../../../../../../icons/icons';
import { Button } from '../../../../../../../ui';
import EditorToolbar from './EditorToolbar';

interface RichTextEditorProps {
  jsonStringTemplate: string;
  validVariables?: { name: string; variable: string }[];
  saveTemplate: (template: string, jsonTemplate: string) => void;
  value?: string;
  onEditorReady?: (editor: any) => void;
  buttonText?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  jsonStringTemplate,
  validVariables,
  saveTemplate,
  value,
  onEditorReady,
  buttonText = 'Save',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Write something...' }),
      Image.configure({ allowBase64: true }),
      ImageResize,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
    ],
    editorProps: {
      attributes: {
        class:
          'prose max-w-none min-h-[220px] [&_ol]:list-decimal [&_ul]:list-disc rounded-lg border border-slate-200 bg-white p-3 text-sm leading-6 focus:outline-none sm:min-h-[260px] sm:p-4',
      },
    },
    content: value ? value : JSON.parse(jsonStringTemplate),
  });

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  const handleInsertVariable = useCallback(
    (variable: string) => {
      if (editor && variable.length !== 0) {
        const variableText = `{{${variable}}}`;
        editor.chain().focus().insertContent(variableText).run();
      }
    },
    [editor],
  );

  const handleInsertImage = useCallback(
    (src: string) => {
      if (editor) {
        editor.chain().focus().setImage({ src }).run();
      }
    },
    [editor],
  );

  const setLink = useCallback(
    (url: string) => {
      if (!editor) return;
      if (!url) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: `{{${url}}}` })
          .run();
      }
    },
    [editor],
  );

  const exportToHTML = () => {
    if (editor) {
      const html = editor.getHTML();
      const htmlJson = JSON.stringify(editor.getJSON());
      if (jsonStringTemplate !== htmlJson) {
        saveTemplate(html, htmlJson);
      }
    }
  };

  const handleVariableClick = (variableName: string, variable: string) => {
    if (variableName === 'Company Logo') return handleInsertImage(variable);
    if (variableName === 'Assessment Link') return setLink(variable);
    if (variableName === 'Unset Link')
      return editor?.chain().focus().unsetLink().run();
    return handleInsertVariable(variable);
  };

  return (
    <>
      <EditorToolbar editor={editor} />

      <div className='relative grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_240px]'>
        <div className='min-w-0 order-last lg:order-first'>
          <EditorContent editor={editor} />
        </div>
        <div className='rounded-lg border border-slate-100 bg-primary-background p-3'>
          <h3 className='mb-2 text-sm font-semibold text-slate-900'>
            Insert Variables
          </h3>
          <div className='min-w-0'>
            { validVariables && (
              <div className='grid grid-cols-2 gap-2 sm:flex sm:flex-wrap lg:grid lg:grid-cols-1'>
                { validVariables.map((variable) => (
                  <button
                      key={variable.variable}
                      onClick={() =>
                        handleVariableClick(variable.name, variable.variable)
                      }
                      className='flex min-h-8 min-w-0 items-center justify-center gap-1.5 rounded-full border border-primary-main bg-white px-2.5 py-1.5 text-center text-[11px] font-medium leading-tight text-primary-main transition hover:bg-emerald-50 sm:w-auto sm:text-xs lg:w-full lg:justify-start lg:text-left'
                  >
                    <AddCircleIcons className='h-3.5 w-3.5 shrink-0' />
                    <span className='min-w-0 break-words'>{ variable.name }</span>
                  </button>
                )) }
              </div>
            ) }
          </div>
        </div>
      </div>
      <div className='mt-3 flex w-full justify-end border-t pt-3'>
        <Button
            variant='primary'
            label={buttonText}
            onClick={exportToHTML}
            className='min-h-9 w-full px-3 py-1.5 text-sm sm:w-auto'
        />
      </div>
    </>
  );
};

export default RichTextEditor;
